import React from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import styled from 'styled-components/macro'
import SearchResultItem from 'components/ui/SearchResultItem'
import { t } from 'ttag'
import SearchResultTitle from 'components/ui/SearchResultTitle'
import SearchResultListing from 'components/ui/SearchResultListing'
import SearchResultContent from 'components/ui/SearchResultContent'
import ExtendedSearchResultInfo from 'components/ui/ExtendedSearchResultInfo'
import InfoCol from 'components/ui/InfoCol'
import SearchResultSelectedFilters from 'components/ui/SearchResultSelectedFilters'
import SelectedFilters from 'components/SelectedFilters'
import ResultInfo from 'components/ui/ResultInfo'
import CustomPagination from 'components/CustomPagination'
import SearchFooter from 'components/ui/SearchFooter'
import ResultActions from 'components/ui/ResultActions'
import ResultAction from 'components/ui/ResultAction'
import ResultActionIcon from 'components/ui/ResultActionIcon'
import ResultUrlContainer from 'components/ui/ResultUrlContainer'
import LoadingSpinner from 'components/ui/LoadingSpinner'
import { getResultUrl } from 'util/hash'
import { RESULTS_PER_PAGE } from 'store/constants/constants'
import SpinnerContainer from 'components/ui/SpinnerContainer'
import { show } from 'redux-modal'

const Link = styled.a`
  color: #003399;
  display: block;
  text-decoration: none;
`

const ResultTitle = styled.div`
  color: ${props => (props.disabled ? 'grey' : '#003399')};
  font-weight: bold;
  font-size: 16px;
  padding: 4px 0;
  ${props => props.disabled && 'cursor: not-allowed'}
`
const ProgrammeSearchResultContent = styled(SearchResultContent)`
  padding: 12px;
  min-height: 68px;
`
const Loading = styled.div`
  position: relative !important;
  margin-top: 50px;
`
const ErrorText = styled.p`
  color: #f31100;
`
const InfoLink = styled.a`
  margin-left: 10px;
  cursor: pointer;
`

function ProgrammeSearchResults({
  searchResults: { results = [], count, current, next, previous, total_pages: totalPages },
  isPending,
  error,
  isSidebarVisible,
  onPageChange,
  onExportToExcel,
  currentFilters,
  onGetResultUrl,
  resultHash,
  isExcelExportPending
}) {
  const dispatch = useDispatch()
  const filters = useSelector(state => state.filters)
  const { currentSearchable } = filters
  let resultsComponent
  if (results.length === 0) {
    resultsComponent = <b>{t`No results found.`}</b>
  } else {
    resultsComponent = (
      <>
        <SearchResultListing>
          {results.map(result => (
            <SearchResultItem key={result.id}>
              <ProgrammeSearchResultContent>
                {result.disabled ? (
                  <ResultTitle disabled={true}>{result.title}</ResultTitle>
                ) : (
                  <Link target="_blank" href={`/programmes/${result.id}/`}>
                    <ResultTitle>{result.title}</ResultTitle>
                  </Link>
                )}
              </ProgrammeSearchResultContent>
            </SearchResultItem>
          ))}
        </SearchResultListing>
        {count > RESULTS_PER_PAGE && (
          <SearchFooter>
            <CustomPagination
              onPageChange={data => onPageChange(data)}
              count={count}
              current={current}
              next={next}
              previous={previous}
              totalPages={totalPages}
            />
          </SearchFooter>
        )}
      </>
    )
  }

  if (error) {
    resultsComponent = <ErrorText>{t`An unexpected error occurred.`}</ErrorText>
  }

  return (
    <>
      <SearchResultTitle>
        {currentSearchable === 'countries_and_regions' ? null : t`Search results - programmes `}
      </SearchResultTitle>
      <ExtendedSearchResultInfo>
        <InfoCol>
          <p>
            {t`
            Click on any entry below to get to the full data on a programme, 
            its thematic information, its indicators and its geography, 
            out of all Interreg non-Interreg - the latter among pre-accession countries 
            - since 2000. Or export the table below to Excel and get aggregated data on 
            all the programmes of your interest.`}
          </p>
          {!isPending && !error && (
            <ResultInfo>{t`${count
              .toString()
              .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} programmes found`}</ResultInfo>
          )}
          {!isSidebarVisible && (
            <SearchResultSelectedFilters>
              <SelectedFilters currentFilters={currentFilters} />
            </SearchResultSelectedFilters>
          )}
        </InfoCol>
      </ExtendedSearchResultInfo>
      {isPending ? (
        <Loading>
          <LoadingSpinner showText />
        </Loading>
      ) : (
        <>
          <ResultActions>
            <ResultAction onClick={onExportToExcel} id="prog_to_excel">
              {t`Export to Excel`}{' '}
              {isExcelExportPending ? (
                <SpinnerContainer>
                  <LoadingSpinner showText={false} />
                </SpinnerContainer>
              ) : (
                <ResultActionIcon className="far fa-file-excel" />
              )}
            </ResultAction>
            <>
              {resultHash === '' ? (
                <ResultAction onClick={onGetResultUrl} id="prog_get_result_url">
                  {t`Get result url`} <ResultActionIcon className="far fa-external-link" />
                </ResultAction>
              ) : (
                <>
                  <ResultUrlContainer
                    onClick={e => e.target.select()}
                    defaultValue={getResultUrl(resultHash)}
                  />
                  <InfoLink
                    target="_blank"
                    onClick={async () => {
                      const data = await fetch(
                        `/wp-json/wp/v2/pages?slug=url-get-results-url-how-to-shorten-the-urls`
                      )
                      const jsonData = await data.json()
                      const msg = jsonData[0].content.rendered
                      dispatch(show('infoModal', { message: msg }))
                    }}
                  >{t`How to shorten the URLs?`}</InfoLink>
                </>
              )}
            </>
          </ResultActions>
          {resultsComponent}
        </>
      )}
    </>
  )
}

ProgrammeSearchResults.propTypes = {
  searchResults: PropTypes.shape({
    results: PropTypes.array,
    count: PropTypes.number,
    current: PropTypes.number,
    next: PropTypes.string,
    previous: PropTypes.string,
    total_pages: PropTypes.number
  }),
  isPending: PropTypes.bool,
  error: PropTypes.bool,
  isSidebarVisible: PropTypes.bool,
  onPageChange: PropTypes.func,
  onExportToExcel: PropTypes.func,
  currentFilters: PropTypes.array,
  onGetResultUrl: PropTypes.func,
  resultHash: PropTypes.string,
  isExcelExportPending: PropTypes.bool
}

export default ProgrammeSearchResults
